/**
 * The company-identity half of an admin's `users` row.
 *
 * Each company in the agency is a separate legal entity with its own KVK/VAT
 * registration and IBAN, so when an admin switches company these fields must
 * follow — otherwise invoices carry one entity's line items under another's
 * letterhead, and the EPC payment QR points at the wrong bank account.
 *
 * The column list is explicit and deliberately excludes every identity field
 * (`id`, `email`, `role`, `full_name`, `avatar_url`, `allowed_pages`, ...), so
 * overlaying a profile can never change who the signed-in user is.
 */

import { supabase } from './supabase';
import { createContextLogger } from './logger';
import type { User } from '../types';

const log = createContextLogger('CompanyProfile');

export const COMPANY_PROFILE_COLUMNS = [
  'company_name',
  'address',
  'kvk_number',
  'vat_number',
  'bank_account',
  'pricing_enabled',
  'tax_percentage',
  'payment_terms_days',
  'company_address',
  'company_phone',
  'company_email',
  'company_tax_id',
  'company_website',
  'logo_url',
  'legal_entity_name',
  'company_street',
  'company_house_number',
  'company_postal_code',
  'company_city',
  'company_country',
  'warehouse_name',
  'warehouse_contact_name',
  'warehouse_street',
  'warehouse_house_number',
  'warehouse_house_number_ext',
  'warehouse_postal_code',
  'warehouse_city',
  'warehouse_country_code',
  'warehouse_phone',
  'warehouse_email',
  'iban',
  'bic',
  'bank_name',
  'reservation_pdf_cc_emails',
] as const;

export type CompanyProfile = Pick<User, (typeof COMPANY_PROFILE_COLUMNS)[number]>;

/** Loads one company's public-facing profile, or null when it cannot be read. */
export async function fetchCompanyProfile(adminId: string): Promise<CompanyProfile | null> {
  if (!adminId) return null;

  const { data, error } = await supabase
    .from('users')
    .select(COMPANY_PROFILE_COLUMNS.join(', '))
    .eq('id', adminId)
    .maybeSingle();

  if (error) {
    log.error('Failed to load company profile', { adminId, error: error.message });
    return null;
  }

  return (data as CompanyProfile | null) ?? null;
}
